// @flow

import { startRequest, finishRequest } from "@store/utils";
import type { MyAbsencesState } from "./types";

import {
  GetMyAbsences,
  GetMyAbsencesSuccess,
  GetMyAbsencesError,
  GetMyAbsencesSchemaSuccess,
  GetMyAbsenceSuccess,
  SetShowAddMyAbsencesModal,
  SetMyAbsenceDraft,
  GetUserActiveShiftPatternSuccess,
  GetMyClassificationsSchemaSuccess,
  CreateMyAbsence,
  CreateMyAbsenceSuccess,
  CreateMyAbsenceError,
  SetShowCloseMyAbsencesModal,
  SetShowReOpenMyAbsencesModal,
  SetShowCancelMyAbsencesModal,
  SetAbsenceStatus,
  SetAbsenceStatusSuccess,
  SetAbsenceStatusError,
  GetMyStandardQuestionsSuccess,
  GetMyDynamicQuestionsSuccess,
  GetMyReferralQuestionsSuccess,
  SetMyCurrentAbsence,
  GetMyAbsenceDaysLost,
  GetMyAbsenceDaysLostSuccess,
  GetMyAbsenceDaysLostError,
  UpdateMyAbsence,
  UpdateMyAbsenceSuccess,
  UpdateMyAbsenceError,
  ExportMyAbsences,
  ExportMyAbsencesSuccess,
  ExportMyAbsencesError,
  GetCanOpenAbsenceSuccess,
} from "@modules/profile/constants/actions";
import { cloneObject } from "@utils/object";

export const initialState: MyAbsencesState = {
  requestsInProgress: 0,
  list: {
    content: [],
    pagination: {
      sort: { orders: [] },
      pageNumber: 0,
      pageSize: 0,
      offset: 0,
    },
    totalElements: 0,
  },
  modals: {
    addAbsence: {
      visible: false,
      editing: false,
    },
    closeAbsence: {
      visible: false,
      editing: false,
    },
    reOpenAbsence: {
      visible: false,
      editing: false,
    },
    cancelAbsence: {
      visible: false,
      editing: false,
    },
  },
  draft: null,
  absencesSchema: null,
  classificationsSchema: null,
  activeShiftPattern: null,
  currentAbsence: null,
  standardQuestions: null,
  dynamicQuestions: null,
  referralQuestions: null,
  additionalInformation: null,
  canOpenAbsence: false,
};

const setLoading = (state: MyAbsencesState) => () => {
  return {
    ...startRequest(state),
  };
};

export const setLoaded = (state: MyAbsencesState) => () => {
  return {
    ...finishRequest(state),
  };
};

const onError = (state: MyAbsencesState) => ({ payload }: Object) => {
  return {
    ...finishRequest(state),
    error: payload,
  };
};

export const onGetMyAbsencesSuccess = (state: MyAbsencesState) => ({ payload }: Object) => {
  return {
    ...state,
    ...finishRequest(state),
    error: null,
    list: payload,
  };
};

export const onGetMyAbsencesSchemaSuccess = (state: MyAbsencesState) => ({ payload }: Object) => {
  return {
    ...state,
    absencesSchema: payload,
  };
};

export const onGetMyClassificationsSchemaSuccess = (state: MyAbsencesState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    classificationsSchema: payload,
  };
};

export const onSetShowAddMyAbsencesModal = (state: MyAbsencesState) => ({ payload }: Object) => {
  let { draft, currentAbsence } = state;
  if (payload.show === false) {
    // close the modal and reset the state
    draft = null;
    currentAbsence = null;
  }

  return {
    ...state,
    draft,
    currentAbsence,
    modals: {
      ...state.modals,
      addAbsence: {
        ...state.modals.addAbsence,
        visible: payload.show,
        editing: payload.isEdit || false,
      },
    },
  };
};

export const onSetShowCloseMyAbsencesModal = (state: MyAbsencesState) => ({ payload }: Object) => {
  return {
    ...state,
    modals: {
      ...state.modals,
      closeAbsence: {
        ...state.modals.closeAbsence,
        visible: payload,
      },
    },
  };
};

export const onSetShowReOpenMyAbsencesModal = (state: MyAbsencesState) => ({ payload }: Object) => {
  return {
    ...state,
    modals: {
      ...state.modals,
      reOpenAbsence: {
        ...state.modals.reOpenAbsence,
        visible: payload,
      },
    },
  };
};

export const onSetShowCancelMyAbsencesModal = (state: MyAbsencesState) => ({ payload }: Object) => {
  return {
    ...state,
    modals: {
      ...state.modals,
      cancelAbsence: {
        ...state.modals.cancelAbsence,
        visible: payload,
      },
    },
  };
};

export const onSetMyAbsenceDraft = (state: MyAbsencesState) => ({ payload }: Object) => {
  return {
    ...state,
    draft: payload,
  };
};

export const onGetMyAbsenceDaysLost = (state: MyAbsencesState) => ({ payload }: Object) => {
  const mapper = {
    Monday: "MON",
    Tuesday: "TUE",
    Wednesday: "WED",
    Thursday: "THU",
    Friday: "FRI",
    Saturday: "SAT",
    Sunday: "SUN",
  };

  return {
    ...state,
    ...finishRequest(state),
    draft: {
      ...state.draft,
      daysLost: payload.map(el => ({
        ...el,
        day: mapper[el.day],
      })),
    },
  };
};

export const onGetUserActiveShiftPatternSuccess = (state: MyAbsencesState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    activeShiftPattern: payload,
  };
};

export const onCreateMyAbsenceSuccess = (state: MyAbsencesState) => ({ payload }: Object) => {
  return {
    ...state,
    ...finishRequest(state),
    currentAbsence: payload,
  };
};

export const onUpdateMyAbsenceSuccess = (state: MyAbsencesState) => ({ payload }: Object) => {
  return {
    ...state,
    ...finishRequest(state),
    currentAbsence: payload,
  };
};

export const onGetMyStandardQuestionsSuccess = (state: MyAbsencesState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    standardQuestions: payload,
  };
};

export const onGetMyDynamicQuestionsSuccess = (state: MyAbsencesState) => ({ payload }: Object) => {
  return {
    ...state,
    dynamicQuestions: payload,
  };
};

export const onGetMyReferralQuestionsSuccess = (state: MyAbsencesState) => ({
  payload,
}: Object) => {
  return {
    ...state,
    referralQuestions: payload,
  };
};

export const onSetMyCurrentAbsence = (state: MyAbsencesState) => ({ payload }: Object) => {
  return {
    ...state,
    currentAbsence: payload,
  };
};

export const onGetMyAbsenceSuccess = (state: MyAbsencesState) => ({ payload }: Object) => {
  return {
    ...state,
    absenceDetails: payload,
  };
};
export const onGetCanOpenAbsenceSuccess = (state: MyAbsencesState) => ({ payload }: Object) => {
  return {
    ...state,
    canOpenAbsence: payload,
  };
};

export default (state: MyAbsencesState = initialState, action: Object): MyAbsencesState => {
  const act = {
    [GetMyAbsences]: setLoading,
    [GetMyAbsencesSuccess]: onGetMyAbsencesSuccess,
    [GetMyAbsencesError]: onError,

    [GetCanOpenAbsenceSuccess]: onGetCanOpenAbsenceSuccess,

    [GetMyAbsencesSchemaSuccess]: onGetMyAbsencesSchemaSuccess,
    [GetMyClassificationsSchemaSuccess]: onGetMyClassificationsSchemaSuccess,
    [SetShowAddMyAbsencesModal]: onSetShowAddMyAbsencesModal,
    [SetShowCloseMyAbsencesModal]: onSetShowCloseMyAbsencesModal,
    [SetShowReOpenMyAbsencesModal]: onSetShowReOpenMyAbsencesModal,
    [SetShowCancelMyAbsencesModal]: onSetShowCancelMyAbsencesModal,
    [SetMyAbsenceDraft]: onSetMyAbsenceDraft,
    [GetUserActiveShiftPatternSuccess]: onGetUserActiveShiftPatternSuccess,

    [CreateMyAbsence]: setLoading,
    [CreateMyAbsenceSuccess]: onCreateMyAbsenceSuccess,
    [CreateMyAbsenceError]: onError,
    [UpdateMyAbsence]: setLoading,
    [UpdateMyAbsenceSuccess]: onUpdateMyAbsenceSuccess,
    [UpdateMyAbsenceError]: onError,

    [SetAbsenceStatus]: setLoading,
    [SetAbsenceStatusSuccess]: setLoaded,
    [SetAbsenceStatusError]: onError,
    [GetMyStandardQuestionsSuccess]: onGetMyStandardQuestionsSuccess,
    [GetMyDynamicQuestionsSuccess]: onGetMyDynamicQuestionsSuccess,
    [GetMyReferralQuestionsSuccess]: onGetMyReferralQuestionsSuccess,

    [SetMyCurrentAbsence]: onSetMyCurrentAbsence,

    [GetMyAbsenceSuccess]: onGetMyAbsenceSuccess,
    [GetMyAbsenceDaysLost]: setLoading,
    [GetMyAbsenceDaysLostSuccess]: onGetMyAbsenceDaysLost,
    [GetMyAbsenceDaysLostError]: onError,

    [ExportMyAbsences]: setLoading,
    [ExportMyAbsencesSuccess]: setLoaded,
    [ExportMyAbsencesError]: onError,
  };
  if (action && action.type && act && act[action.type]) {
    return act[action.type](cloneObject(state))(action);
  }
  return state;
};
